import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { packageBooking } from '@/lib/apiconfig';
import { postApi } from '@/lib/apiHelper';

const PackageBookingForm = ({ packageName, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        adults: '2',
        childrenBelow15: '0',
        numberOfNights: '2',
        foodOptions: {
            breakfast: false,
            lunch: false,
            dinner: false,
            tea: false
        },
        transportRequired: false,
        campfireRequired: false,
        additionalInfo: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFoodOptionChange = (option) => {
        setFormData(prev => ({
            ...prev,
            foodOptions: {
                ...prev.foodOptions,
                [option]: !prev.foodOptions[option]
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const bookingData = {
            ...formData,
            packageName: packageName,
            foodOptions: Object.keys(formData.foodOptions)
                .filter(key => formData.foodOptions[key])
                .join(', ')
        };

        postApi(packageBooking, bookingData).then((result) => {
            if (result.success) {
                toast.success(`Package Booking Submitted! Your booking request (${result.bookingId}) has been received. We'll contact you shortly.`);

                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    adults: '2',
                    childrenBelow15: '0',
                    numberOfNights: '2',
                    foodOptions: {
                        breakfast: false,
                        lunch: false,
                        dinner: false,
                        tea: false
                    },
                    transportRequired: false,
                    campfireRequired: false,
                    additionalInfo: ''
                });

                if (onClose) {
                    setTimeout(() => onClose(), 2000);
                }
            } else {
                toast.error(result?.message || 'Failed to submit booking. Please try again.');
            }
        }).catch(() => {
            toast.error('Failed to send message. Please try again.');
        })
        .finally(() => {
            setLoading(false);
        });
    };

    return (
        <div className="package-booking-overlay">
            <div className="package-booking-form-container">
                <div className="package-booking-form-header">
                    <div>
                        <h3 className="package-booking-form-title">Book Package</h3>
                        <p className="package-booking-form-subtitle">{packageName}</p>
                    </div>
                    {onClose && (
                        <button className="package-booking-close" onClick={onClose}>
                            <X size={24} />
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="package-booking-form">
                    <div className="form-row">
                        <div className="form-group">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter your name" required />
                        </div>

                        <div className="form-group">
                            <Label htmlFor="email">Email Address *</Label>
                            <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" required />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" required />
                        </div>

                        <div className="form-group">
                            <Label htmlFor="numberOfNights">Number of Nights *</Label>
                            <Select value={formData.numberOfNights} onValueChange={(value) => setFormData(prev => ({ ...prev, numberOfNights: value }))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                                        <SelectItem key={num} value={num.toString()}>
                                            {num} {num === 1 ? 'Night' : 'Nights'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <Label htmlFor="adults">Number of Adults *</Label>
                            <Select value={formData.adults} onValueChange={(value) => setFormData(prev => ({ ...prev, adults: value }))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[1, 2, 3, 4, 5, 6, 8, 10, 15, 20, 25, 30].map((num) => (
                                        <SelectItem key={num} value={num.toString()}>
                                            {num} {num === 1 ? 'Adult' : 'Adults'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label htmlFor="childrenBelow15">Children Below 15</Label>
                            <Select value={formData.childrenBelow15} onValueChange={(value) => setFormData(prev => ({ ...prev, childrenBelow15: value }))}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[0, 1, 2, 3, 4, 5, 6, 8, 10].map((num) => (
                                        <SelectItem key={num} value={num.toString()}>
                                            {num} {num === 1 ? 'Child' : 'Children'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="form-group">
                        <Label>Food Options</Label>
                        <div className="food-options-grid">
                            <div className="checkbox-item">
                                <Checkbox id="breakfast" checked={formData.foodOptions.breakfast} onCheckedChange={() => handleFoodOptionChange('breakfast')} />
                                <label htmlFor="breakfast" className="checkbox-label">Breakfast</label>
                            </div>

                            <div className="checkbox-item">
                                <Checkbox id="lunch" checked={formData.foodOptions.lunch} onCheckedChange={() => handleFoodOptionChange('lunch')} />
                                <label htmlFor="lunch" className="checkbox-label">Lunch</label>
                            </div>

                            <div className="checkbox-item">
                                <Checkbox id="dinner" checked={formData.foodOptions.dinner} onCheckedChange={() => handleFoodOptionChange('dinner')} />
                                <label htmlFor="dinner" className="checkbox-label">Dinner</label>
                            </div>

                            <div className="checkbox-item">
                                <Checkbox id="tea" checked={formData.foodOptions.tea} onCheckedChange={() => handleFoodOptionChange('tea')} />
                                <label htmlFor="tea" className="checkbox-label">Tea/Snacks</label>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <Label>Additional Services</Label>
                        <div className="additional-services-grid">
                            <div className="checkbox-item">
                                <Checkbox id="transport" checked={formData.transportRequired} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, transportRequired: checked }))} />
                                <label htmlFor="transport" className="checkbox-label">Transport Required for Local Visit</label>
                            </div>

                            <div className="checkbox-item">
                                <Checkbox id="campfire" checked={formData.campfireRequired} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, campfireRequired: checked }))} />
                                <label htmlFor="campfire" className="checkbox-label">Campfire Arrangement</label>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <Label htmlFor="additionalInfo">Additional Information</Label>
                        <Textarea id="additionalInfo" name="additionalInfo" value={formData.additionalInfo} onChange={handleInputChange} placeholder="Any special requests or additional information..." rows={4} />
                    </div>

                    <div className="package-booking-form-actions">
                        {onClose && (
                            <Button type="button" variant="outline" onClick={onClose} className="cancel-btn">
                                Cancel
                            </Button>
                        )}
                        <Button type="submit" className="submit-package-booking-btn" disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" size={20} />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Booking Request'
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PackageBookingForm;
